// What a form offers: which request types (on the request-type field) and which countries (on the location field).
// Nothing chosen = everything active. Saved in the field's config, enforced by the form engine and the backend.
import { Autocomplete, Chip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { FieldOption } from '@re/form-engine';
import type { Lookups } from '@re/form-ui';
import { api } from '../../api/client';
import type { LocationRow } from '../../api/types';
import { TextField } from '../../components/text-field';
import { isEn, tr } from '../../lib/i18n';

const DIRECTION: Record<string, [string, string]> = {
  OFFER: ['أعرض عقاراً', 'Offering a property'],
  DEMAND: ['أبحث عن عقار', 'Looking for a property'],
  SERVICE: ['خدماتنا', 'Services'],
};
const ORDER = ['OFFER', 'DEMAND', 'SERVICE'];

type RT = Lookups['requestTypes'][number];

export function RequestTypesPicker({ lookups, value, onChange }: { lookups?: Lookups; value: FieldOption[] | undefined; onChange: (o: FieldOption[] | undefined) => void }) {
  const types = [...(lookups?.requestTypes ?? [])].sort((a, b) => ORDER.indexOf(a.direction) - ORDER.indexOf(b.direction));
  const codes = (value ?? []).map((o) => o.value);
  const chosen = types.filter((t) => codes.includes(t.code));
  const name = (t: RT) => (isEn() ? t.name_en || t.name_ar : t.name_ar);
  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={types}
      value={chosen}
      groupBy={(t) => tr(...(DIRECTION[t.direction] ?? [t.direction, t.direction]))}
      getOptionLabel={name}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      onChange={(_, v) => onChange(v.length ? v.map((t) => ({ value: t.code, label: { ar: t.name_ar, ...(t.name_en ? { en: t.name_en } : {}) } })) : undefined)}
      renderTags={(v, getTagProps) => v.map((t, i) => <Chip {...getTagProps({ index: i })} key={t.code} size="small" label={name(t)} />)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={tr('أنواع الطلب في هذا النموذج', 'Request types in this form')}
          placeholder={chosen.length ? undefined : tr('كل الأنواع', 'All types')}
          helperText={tr('اتركها فارغة لعرض كل الأنواع. مثال: شراء وإيجار فقط لمكتب لا يستقبل عروض بيع.', 'Leave empty for every type. E.g. only Buy and Rent for an office that takes no sale offers.')}
        />
      )}
    />
  );
}

export function CountriesPicker({ value, onChange }: { value: string[] | undefined; onChange: (c: string[] | undefined) => void }) {
  const q = useQuery({ queryKey: ['countries'], queryFn: () => api<{ items: LocationRow[] }>('/countries', { query: { includeInactive: false } }) });
  const all = q.data?.items ?? [];
  const chosen = all.filter((c) => c.iso2 && (value ?? []).includes(c.iso2));
  const name = (c: LocationRow) => (isEn() ? c.name_en || c.name_ar : c.name_ar);
  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      loading={q.isLoading}
      options={all}
      value={chosen}
      getOptionLabel={name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_, v) => onChange(v.length ? v.map((c) => c.iso2!).filter(Boolean) : undefined)}
      renderTags={(v, getTagProps) => v.map((c, i) => <Chip {...getTagProps({ index: i })} key={c.id} size="small" label={name(c)} />)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={tr('الدول في هذا النموذج', 'Countries in this form')}
          placeholder={chosen.length ? undefined : tr('كل الدول', 'All countries')}
          helperText={tr('اتركها فارغة لكل الدول. دولة واحدة تُختار تلقائياً ولا يظهر سؤال الدولة.', 'Leave empty for every country. With a single country it is chosen automatically and not asked.')}
        />
      )}
    />
  );
}
